import { useRef } from 'react'
import type { InputFieldProps } from './Input.types'

export default function InputField({
  name,
  value,
  label,
  placeholder,
  type = 'text',
  required,
  disabled,
  error,
  helperText,
  clearable = true,
  onChange,
  onClear,
}: InputFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasValue = value !== undefined && value !== ''

  const rootClasses = [
    'rd-input',
    'rd-input--medium',
    'rd-input--floating',
    error ? 'rd-input--error' : '',
    disabled ? 'rd-input--disabled' : '',
    hasValue ? 'has-value' : '',
  ].filter(Boolean).join(' ')

  const labelClasses = [
    'rd-input__label',
    required ? 'rd-input__label--required' : '',
  ].filter(Boolean).join(' ')

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else if (onChange) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, 'value',
      )?.set
      if (nativeInputValueSetter && inputRef.current) {
        nativeInputValueSetter.call(inputRef.current, '')
        const event = new Event('input', { bubbles: true })
        inputRef.current.dispatchEvent(event)
      }
    }
  }

  return (
    <div className={rootClasses}>
      <div className="rd-input__wrapper">
        <input
          ref={inputRef}
          className="rd-input__field"
          type={type}
          name={name}
          value={value}
          placeholder={placeholder || ' '}
          required={required}
          disabled={disabled}
          onChange={onChange}
          autoComplete="off"
        />
        {label && <label className={labelClasses}>{label}</label>}
        {clearable && hasValue && !disabled && (
          <button
            type="button"
            className="rd-input__clear is-visible"
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear"
          >
            <i className="rd-icon-cross" />
          </button>
        )}
      </div>
      {error && <span className="rd-input__error-text">{error}</span>}
      {helperText && !error && <span className="rd-input__helper-text">{helperText}</span>}
    </div>
  )
}
